import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown, Check, ExternalLink } from "lucide-react";

/**
 * Cross-app Modules quick-switch pill.
 *
 * `variant`:
 *   - "light" (default) — for use inside the Unifier chrome (light background)
 *   - "dark"            — for use inside a dark strip (Partners top strip)
 *
 * `current` is the slug of the currently active module ("hub" | "partners" |
 * "formulations" | "dossiers" | "clinical-intelligence").
 */
const MODULES = [
  { slug: "bd", label: "International BD", url: "/bd", internal: true },
  { slug: "hrms", label: "Human Resources", url: "/hrms", internal: true },
  { slug: "portfolio", label: "Portfolio", url: "/portfolio", internal: true },
  { slug: "crm", label: "Customer CRM", url: "/crm", internal: true },
  { slug: "hub",         label: "Hub",              url: "/",              internal: true  },
  { slug: "partners",    label: "Partners",         url: "/partners",      internal: true  },
  { slug: "formulations",label: "Formulations",     url: "/formulations",  internal: true  },
  { slug: "dossiers",    label: "Dossiers",         url: "/dossiers",      internal: true  },
  { slug: "clinical-intelligence", label: "Clinical Intelligence", url: "/clinical-intelligence", internal: true },
];

export default function ModuleSwitcher({ current = "hub", variant = "light" }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const [menuLeft, setMenuLeft] = useState(0);
  useLayoutEffect(() => {
    if (!open) return;
    const position = () => {
      const rect = wrapRef.current.getBoundingClientRect();
      setMenuLeft(Math.max(12, Math.min(rect.right - 256, window.innerWidth - 268)) - rect.left);
    };
    position();
    window.addEventListener('resize', position);
    return () => window.removeEventListener('resize', position);
  }, [open]);

  useEffect(() => {
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isDark = variant === "dark";

  // Pill trigger
  const triggerBase =
    "inline-flex items-center gap-2 whitespace-nowrap border font-mono-meta transition-colors";
  const triggerLight =
    "px-3 py-1.5 border-[hsl(var(--border-strong,215_20%_75%))] text-foreground hover:border-foreground bg-transparent rounded-sm";
  const triggerDark =
    "px-3 py-1 rounded-sm";

  const triggerDarkStyle = {
    border: "1px solid rgba(255,255,255,0.16)",
    color: "hsl(var(--inverse-foreground))",
    background: "rgba(255,255,255,0.04)",
  };

  return (
    // Escape closes the menu and returns focus to its trigger.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div ref={wrapRef} role="group" aria-label="Module switcher" className="relative" data-testid="module-switcher" onKeyDown={event => {
      if (event.key === 'Escape' && open) { event.preventDefault(); setOpen(false); wrapRef.current.querySelector('button')?.focus(); }
    }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${triggerBase} ${isDark ? triggerDark : triggerLight}`}
        style={isDark ? triggerDarkStyle : undefined}
        data-testid="module-switcher-trigger"
        aria-expanded={open}
        aria-label="Switch application"
        onKeyDown={(event) => { if (event.key === "Escape") setOpen(false); }}
      >
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: "var(--vl-accent)" }}
          aria-hidden="true"
        />
        <span>Modules<span className="hidden sm:inline"> · {MODULES.find((m) => m.slug === current)?.label || "Hub"}</span></span>
        <ChevronDown size={12} strokeWidth={1.75} />
      </button>

      {open && (
        <div
          className="absolute top-full mt-2 w-64 z-50 rounded-sm overflow-hidden"
          style={{
            left: menuLeft, maxWidth: "calc(100vw - 24px)", maxHeight: "70vh", overflowY: "auto",
            background: "hsl(var(--inverse, 222 47% 11%))",
            color: "hsl(var(--inverse-foreground, 210 40% 98%))",
            border: "1px solid hsl(var(--inverse, 222 47% 11%))",
            boxShadow: "0 18px 40px -12px rgba(15, 23, 42, 0.35)",
          }}
          data-testid="module-switcher-menu"
        >
          <div
            className="px-4 pt-3 pb-2 font-mono-meta"
            style={{ color: "rgba(248,250,252,0.55)" }}
          >
            Switch Module
          </div>
          {MODULES.map((m) => {
            const active = m.slug === current;
            const linkProps = m.internal
              ? { href: m.url }
              : { href: m.url, target: "_blank", rel: "noopener noreferrer" };
            return (
              <a
                {...linkProps}
                href={m.url}
                key={m.slug}
                className={`flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${active ? "" : "hover:bg-white/5"}`}
                style={{
                  borderTop: "1px solid rgba(255,255,255,0.06)",
                  background: active ? "rgba(249,115,22,0.10)" : "transparent",
                  cursor: active ? "default" : "pointer",
                }}
                aria-current={active ? "true" : undefined}
                onClick={(e) => {
                  if (active) e.preventDefault();
                  setOpen(false);
                }}
                data-testid={`module-switcher-item-${m.slug}`}
              >
                <span className="flex items-center gap-2">
                  <span
                    style={{
                      color: active
                        ? "var(--vl-accent)"
                        : "hsl(var(--inverse-foreground, 210 40% 98%))",
                      fontWeight: active ? 600 : 500,
                    }}
                  >
                    {m.label}
                  </span>
                  {!m.internal && (
                    <span
                      className="font-mono-meta"
                      style={{ color: "rgba(248,250,252,0.5)" }}
                    >
                      external
                    </span>
                  )}
                </span>
                {active ? (
                  <Check size={14} strokeWidth={1.75} style={{ color: "var(--vl-accent)" }} />
                ) : m.internal ? (
                  <span
                    className="font-mono-meta"
                    style={{ color: "rgba(248,250,252,0.4)" }}
                  >
                    →
                  </span>
                ) : (
                  <ExternalLink
                    size={12}
                    strokeWidth={1.75}
                    style={{ color: "rgba(248,250,252,0.45)" }}
                  />
                )}
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
